import type { ReactNode } from "react";

/** A figure for display: numbers get thousands separators, null is the en dash (invariant 11). */
export function figureText(v: ReactNode | null | undefined): ReactNode {
  if (v === null || v === undefined) return "–";
  if (typeof v === "number") return v.toLocaleString();
  return v;
}

/** The word "over", in danger ink and bold — a ceiling passed is never told by colour alone. */
export function Over({ children }: { children: ReactNode }) {
  return <span className="k-over">{children}</span>;
}

interface HeroBase {
  /** 12.8 muted, above the figure: "Trend weight", "Next up" */
  label?: ReactNode;
  /**
   * The one answer. `null` renders "–" in muted — "not measured" is not 0 (invariant 11); say
   * what is missing in `line`. Omit it (undefined) when the hero leads with a `title` instead.
   */
  figure?: ReactNode | null;
  unit?: ReactNode;
  /** instead of a figure: a 20/700 line such as "Core & upper · 32 min" (kit Hero B) */
  title?: ReactNode;
  /** the one qualifier line, 13.8 muted: "−0.35 kg/week · 4.4 kg to go" */
  line?: ReactNode;
  /** a second qualifier line, same style */
  sub?: ReactNode;
  /** one sentence under the qualifiers, in body ink: the reason for an offer in `actions` —
   *  the cycle line's "Period day 2: the gentle session is offered…" (ADR-057) */
  note?: ReactNode;
  /** a Ring, ProgressBar or chart */
  visual?: ReactNode;
  /** put `visual` beside the text (a 72px Ring, kit Hero B) instead of under it */
  visualSide?: boolean;
  /**
   * "over": the qualifier line turns danger ink and bold. For only the word to turn, leave the
   * tone default and wrap the word in `<Over>` inside `line`, as the kit draws it.
   */
  tone?: "default" | "over";
  ariaLabel?: string;
}

/**
 * The one answer a page opens with (SNIPPETS §7) — one per page, never nested, and the only card.
 *
 * When to use: the first block of every page. Pass `actions` (44px Buttons, primary usually
 * `grow`) for a hero that does something, or `onPress` for one that is itself the link. Not both:
 * a pressable hero is a `<button>`, and a button cannot hold buttons.
 */
export function Hero(
  props: HeroBase & ({ actions?: ReactNode; onPress?: never } | { onPress: () => void; actions?: never })
) {
  const { label, figure, unit, title, line, sub, note, visual, visualSide, tone = "default", ariaLabel } = props;
  const text = (
    <>
      {title !== undefined ? <p className="k-hero__title">{title}</p> : null}
      {figure !== undefined || title === undefined ? (
        <p className="k-hero__fig">
          <span className={`k-hero__num${figure === null || figure === undefined ? " is-empty" : ""}`}>
            {figureText(figure)}
          </span>
          {unit && figure !== null && figure !== undefined ? <span className="k-hero__unit">{unit}</span> : null}
        </p>
      ) : null}
      {line !== undefined ? <p className="k-hero__line">{line}</p> : null}
      {sub !== undefined ? <p className="k-hero__line">{sub}</p> : null}
    </>
  );

  const inner = (
    <>
      {label !== undefined ? <p className="k-hero__label">{label}</p> : null}
      {visual && visualSide ? (
        <div className="k-hero__side">
          <div className="k-hero__sidetxt">{text}</div>
          <div className="k-hero__visual">{visual}</div>
        </div>
      ) : (
        <>
          {text}
          {visual ? <div className="k-hero__visual">{visual}</div> : null}
        </>
      )}
      {note !== undefined ? <p className="k-hero__note">{note}</p> : null}
      {"actions" in props && props.actions ? <div className="k-hero__actions">{props.actions}</div> : null}
    </>
  );

  const cls = `k-hero${tone === "over" ? " k-hero--over" : ""}`;
  if ("onPress" in props && props.onPress) {
    return (
      <button type="button" className={`${cls} k-hero--press`} onClick={props.onPress} aria-label={ariaLabel}>
        {inner}
      </button>
    );
  }
  return (
    <section className={cls} aria-label={ariaLabel}>
      {inner}
    </section>
  );
}
